import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Subscription} from 'rxjs';
import {IFaehigkeit} from '../../../shared/model/faehigkeit.model';
import {NgTemplateOutlet} from '@angular/common';
import {WikiPreviewDirective} from '../../../shared/wiki-preview.directive';
import {appLanguage} from '../../../services/format-datepicker';
import {Berater, IBerater} from '../../../shared/model/berater.model';
import {BeraterService} from '../../../services/berater.service';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource
} from '@angular/material/table';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {TranslatePipe} from '@ngx-translate/core';
import {MatCard, MatCardContent} from '@angular/material/card';
import {MessageService} from '../../../shared/components/message.service';
import {MatSort, MatSortHeader} from '@angular/material/sort';
import {MatIcon} from '@angular/material/icon';
import {MatCheckbox} from '@angular/material/checkbox';
import {hasUnratedSkills} from '../../../shared/model/export-skill-rating';
import {
  SKILL_MATRIX_ROWS,
  selectedSkillCategories,
  skillCategoryKey
} from '../../../shared/constants/two-pager-limits';
import {FieldWarningComponent} from '../../../shared/components/field-warning/field-warning.component';
import {VocabularyService} from '../../../services/vocabulary.service';
import {VocabularyPipe} from '../../../shared/vocabulary.pipe';
import {ValidationFinding, findEntryFinding} from '../../../shared/model/validation-finding';

/** NP20-958: Gruppenkopf der zweistufigen Export-Auswahl (Kategorie → Fähigkeiten). */
export interface SkillGroup {
  category: string;
  skills: IFaehigkeit[];
}

/** Zeile der Export-Tabelle: entweder eine Kategorie-Ueberschrift oder eine Fähigkeit. */
export type SkillTableRow = IFaehigkeit | {group: SkillGroup};

@Component({
  selector: 'app-skills',
  imports: [
    MatTable,
    MatHeaderCell,
    MatColumnDef,
    MatCell,
    MatHeaderRow,
    MatRow,
    MatRowDef,
    MatHeaderRowDef,
    MatCellDef,
    MatHeaderCellDef,
    MatButton,
    TranslatePipe,
    MatCard,
    MatCardContent,
    MatSort,
    MatSortHeader,
    MatIconButton,
    MatIcon,
    MatCheckbox,
    MatProgressSpinner,
    WikiPreviewDirective,
    NgTemplateOutlet,
    FieldWarningComponent,
    VocabularyPipe
  ],
  templateUrl: './skills.component.html',
  styleUrl: './skills.component.scss',
  standalone: true
})
export class SkillsComponent implements OnInit, OnChanges {
  @Input() skills: IFaehigkeit[];
  @Input() consultant: Berater;
  @Input() displayedColumns: string[] = ['skill', 'category', 'level'];
  @Input() showLevelBar: boolean = true;
  @Input() showNoWarnings: boolean = false;
  @Input() editable: boolean = true;
  @Input() exportMode: boolean = false;
  @Input() twoPagerMode: boolean = false;
  // Two-Pager: gemeinsame Skill-Auswahl (vom Parent kontrolliert, synchron mit den Task-Skills).
  @Input() selectedSkillIds: string[] = [];
  /**
   * NP20-959: Auswahl einer geladenen Vorlage im STANDARD-Export. Dort haelt die Kachel ihre
   * Haekchen selbst und hat sich beim Betreten des Exportmodus alles vorgewaehlt — ohne diesen
   * Weg zeigte sie nach dem Laden alles an, waehrend das Payload die Teilmenge traegt. Wie in
   * den anderen Kacheln: null bedeutet "nichts zu erzwingen".
   */
  @Input() forcedSelectedIds: string[] | null = null;
  @Input() findings: ValidationFinding[] = [];

  @Output() projectSkill = new EventEmitter<void>();
  @Output() exportSelectionChange = new EventEmitter<string[]>();

  selectedExportIds: string[] = [];

  // NP20-958: Im Export-Modus wird die Auswahl zweistufig nach Kategorie gruppiert angezeigt.
  // Die Liste haengt nur an den Faehigkeiten und wird deshalb bei deren Aenderung neu gebaut,
  // nicht als Getter pro Change-Detection-Zyklus.
  skillGroups: SkillGroup[] = [];

  // In STANDARD verwaltet die Komponente die Auswahl lokal, im Two-Pager kommt sie vom Parent.
  get currentSkillSelection(): string[] {
    return this.twoPagerMode ? (this.selectedSkillIds ?? []) : this.selectedExportIds;
  }

  private readonly destroyRef = inject(DestroyRef);

  /** Zuletzt selbst gesetzte Sortierung (NP20-760); null, sobald der Nutzer selbst sortiert hat. */
  private autoSortedBy: 'level' | 'skill' | null = null;

  /** Nur waehrend des eigenen sort()-Aufrufs true, damit dessen sortChange nicht als Nutzeraktion zaehlt. */
  private applyingAutoSort = false;

  private sortChangeSub?: Subscription;

  private _sort: MatSort | null = null;

  @ViewChild(MatSort)
  set sort(sort: MatSort | null) {
    this._sort = sort;

    if (this._sort) {
      this.dataSource.sort = this._sort;

      // Review PR #648: Jede Sortierung des Nutzers hebt die selbst gesetzte auf — auch auf derselben
      // Spalte, wo der Spaltenvergleich allein die Richtungsumkehr nicht erkennen wuerde.
      this.sortChangeSub?.unsubscribe();
      this.sortChangeSub = this._sort.sortChange
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          if (!this.applyingAutoSort) {
            this.autoSortedBy = null;
          }
        });

      Promise.resolve().then(() => this.applyInitialSort());
    }
  }

  get sort(): MatSort | null {
    return this._sort;
  }

  editMode: boolean = false;
  isSaving = false;
  skillsCopy: IFaehigkeit[];
  originalSkills: IFaehigkeit[];
  formChanged = false;

  dataSource = new MatTableDataSource<IFaehigkeit>();

  private findingBySkill = new Map<IFaehigkeit, ValidationFinding>();

  readonly wikiLang = appLanguage();

  constructor(
    private beraterService: BeraterService,
    private messageService: MessageService,
    private vocabulary: VocabularyService,
  ) {
    this.vocabulary.changes$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.dataSource.data = [...this.dataSource.data];
      this.rebuildSkillGroups();
    });
  }

  ngOnInit(): void {
    this.dataSource.sortingDataAccessor = (item, property) => {
      const locale = this.vocabulary.language;
      switch (property) {
        case 'skill':
          return this.vocabulary.skillName(item).toLocaleLowerCase(locale);
        case 'category':
          return this.vocabulary.skillCategory(item).toLocaleLowerCase(locale);
        case 'level':
          return this.levelToStars(item.level);
        default:
          return '';
      }
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['skills'] && changes['skills'].currentValue) {
      this.skills = changes['skills'].currentValue;
      this.skillsCopy = JSON.parse(JSON.stringify(this.skills));
      this.refreshDataSource();
    }
    if (changes['skills'] || changes['findings']) {
      this.indexFindings();
    }
    if (changes['exportMode'] || changes['twoPagerMode']) {
      if (this.exportMode && !this.twoPagerMode) {
        this.autoSelectAll();
      } else if (!this.exportMode) {
        this.selectedExportIds = [];
        setTimeout(() => this.exportSelectionChange.emit([...this.selectedExportIds]), 0);
      }
      // Two-Pager: Auswahl wird über selectedSkillIds vom Parent gesteuert — kein lokales Reset/Emit.
    } else if (changes['skills'] && this.exportMode && !this.twoPagerMode) {
      this.autoSelectAll();
    }

    // Nach dem Zweig oben, damit die erzwungene Auswahl das autoSelectAll desselben Durchlaufs
    // ueberschreibt. Dessen Emit liest `selectedExportIds` erst im setTimeout — also diesen Wert.
    if (changes['forcedSelectedIds'] && this.forcedSelectedIds !== null && !this.twoPagerMode) {
      this.selectedExportIds = [...this.forcedSelectedIds];
      setTimeout(() => this.exportSelectionChange.emit([...this.selectedExportIds]), 0);
    }
  }

  private indexFindings(): void {
    this.findingBySkill = new Map<IFaehigkeit, ValidationFinding>();
    (this.skills ?? []).forEach((skill, entryIndex) => {
      const finding = findEntryFinding(this.findings, entryIndex, 'level');
      if (finding) this.findingBySkill.set(skill, finding);
    });
  }

  skillFinding(skill: IFaehigkeit | null | undefined): ValidationFinding | null {
    if (this.editMode || this.exportMode || this.showNoWarnings || !skill) return null;
    return this.findingBySkill.get(skill) ?? null;
  }

  private autoSelectAll(): void {
    this.selectedExportIds = (this.skills ?? []).map(s => s.id).filter(Boolean);
    setTimeout(() => this.exportSelectionChange.emit([...this.selectedExportIds]), 0);
  }

  // Der Generator zeigt Skills als Zeilen inkl. Kategorie-Überschrift. Budget = ausgewählte Skills
  // plus je eine Zeile pro distinkter Kategorie (1 Kat.=17, 2 Kat.=16, 3 Kat.=15 …).
  get skillRowsMax(): number {
    return SKILL_MATRIX_ROWS;
  }

  /**
   * Zeilenverbrauch, Kategorien und Auswahl-Ids der aktuellen Auswahl — gemerkt an den
   * Referenzen von `skills` und Auswahl-Array.
   *
   * `canSelectSkill` haengt als `[disabled]` an jeder Tabellenzeile. Ohne dieses Merken lief
   * pro Change-Detection-Lauf und Zeile eine Zaehlung ueber alle Fähigkeiten — bei einem
   * Berater mit vielen Fähigkeiten quadratisch. Jetzt einmal O(N), danach O(1) je Zeile.
   */
  private selectionMemo: {
    skills: IFaehigkeit[] | undefined;
    ids: readonly string[];
    selected: Set<string>;
    categories: Set<string>;
    usedRows: number;
  } | null = null;

  private get selectionState(): {selected: Set<string>; categories: Set<string>; usedRows: number} {
    const skills = this.skills;
    const ids = this.currentSkillSelection;
    if (!this.selectionMemo || this.selectionMemo.skills !== skills || this.selectionMemo.ids !== ids) {
      const categories = selectedSkillCategories(skills, ids);
      this.selectionMemo = {
        skills,
        ids,
        selected: new Set(ids),
        categories,
        usedRows: ids.length + categories.size
      };
    }
    return this.selectionMemo;
  }

  isSkillSelected(skill: IFaehigkeit): boolean {
    return skill?.id !== undefined && this.selectionState.selected.has(skill.id);
  }

  canSelectSkill(skill: IFaehigkeit): boolean {
    if (!this.twoPagerMode) return true;
    if (this.isSkillSelected(skill)) return true;
    // Die Fähigkeit belegt eine Zeile, eine noch nicht vertretene Kategorie eine zweite.
    const {categories, usedRows} = this.selectionState;
    const needed = categories.has(skillCategoryKey(skill)) ? 1 : 2;
    return usedRows + needed <= SKILL_MATRIX_ROWS;
  }

  // Für den Hinweis: true, sobald mindestens eine noch nicht gewählte Fähigkeit wegen des Budgets nicht mehr wählbar ist.
  get skillBudgetReached(): boolean {
    if (!this.exportMode || !this.twoPagerMode) return false;
    return (this.skills ?? []).some(s => !this.isSkillSelected(s) && !this.canSelectSkill(s));
  }

  toggleExportSelection(id: string, checked: boolean): void {
    const current = this.currentSkillSelection;
    const next = checked
      ? (current.includes(id) ? [...current] : [...current, id])
      : current.filter(i => i !== id);
    this.applyExportSelection(next);
  }

  /**
   * NP20-958: Toggle "alle in Kategorie". Im Two-Pager begrenzt das Zeilenbudget die Auswahl —
   * es werden so viele Fähigkeiten der Kategorie übernommen, wie noch hineinpassen. Der Parent
   * lehnt eine zu grosse Menge sonst komplett ab und der Toggle bliebe wirkungslos.
   */
  toggleCategoryExport(group: SkillGroup, checked: boolean): void {
    const groupIds = group.skills.map(s => s.id).filter(Boolean);
    if (!checked) {
      const removed = new Set(groupIds);
      this.applyExportSelection(this.currentSkillSelection.filter(id => !removed.has(id)));
      return;
    }
    // Budget inkrementell fortschreiben, statt es je Kandidat neu ueber alle Fähigkeiten zu zaehlen.
    const next = [...this.currentSkillSelection];
    const selected = new Set(next);
    const categories = selectedSkillCategories(this.skills, next);
    let usedRows = next.length + categories.size;
    for (const skill of group.skills) {
      const id = skill.id;
      if (id === undefined || selected.has(id)) continue;
      const category = skillCategoryKey(skill);
      const needed = categories.has(category) ? 1 : 2;
      if (this.twoPagerMode && usedRows + needed > SKILL_MATRIX_ROWS) break;
      next.push(id);
      selected.add(id);
      categories.add(category);
      usedRows += needed;
    }
    this.applyExportSelection(next);
  }

  private applyExportSelection(next: string[]): void {
    // Controlled im Two-Pager (Parent hält die Auswahl), lokal im STANDARD-Export.
    if (!this.twoPagerMode) {
      this.selectedExportIds = next;
    }
    this.exportSelectionChange.emit([...next]);
  }

  selectedCountInGroup(group: SkillGroup): number {
    return group.skills.filter(s => this.isSkillSelected(s)).length;
  }

  isCategoryFullySelected(group: SkillGroup): boolean {
    return group.skills.length > 0 && this.selectedCountInGroup(group) === group.skills.length;
  }

  isCategoryPartiallySelected(group: SkillGroup): boolean {
    const count = this.selectedCountInGroup(group);
    return count > 0 && count < group.skills.length;
  }

  canToggleCategory(group: SkillGroup): boolean {
    if (!this.twoPagerMode) return true;
    if (this.selectedCountInGroup(group) > 0) return true;
    return group.skills.some(s => this.canSelectSkill(s));
  }

  get hasUnratedSelectedSkills(): boolean {
    return hasUnratedSkills(this.skills, this.currentSkillSelection);
  }

  /**
   * NP20-959: Die zweistufige Auswahl laeuft in derselben Tabelle wie die Leseansicht. Die
   * Kategorie wird dabei nicht zur Spalte, sondern zur eigenen Zeile mit dem Toggle "alle in
   * Kategorie" — so bleibt die Trennung sichtbar, ohne eine zweite Darstellung zu pflegen.
   *
   * Nur im Two-Pager: dort erzwingt das Zeilenbudget die Auswahl entlang der Kategorien. Im
   * Standard-Export ist alles vorgewaehlt und es wird einzeln abgewaehlt — der behaelt deshalb
   * seine flache, sortierbare Tabelle samt Kategorie-Spalte.
   */
  get groupedExportSelection(): boolean {
    return this.exportMode && this.twoPagerMode;
  }

  get effectiveColumns(): string[] {
    if (this.groupedExportSelection) return ['select', 'skill', 'level'];
    return this.exportMode ? ['select', ...this.displayedColumns] : this.displayedColumns;
  }

  /**
   * Zeilen der Export-Tabelle. Ein Feld, kein Getter: CdkTable vergleicht die dataSource per
   * Identitaet und wuerde bei einem neuen Array pro Change-Detection-Lauf die ganze Tabelle
   * neu aufbauen — dabei geht der Fokus aus der gerade geklickten Checkbox verloren.
   * Die Zeilen haengen nur an den Gruppen, nicht an der Auswahl; die Haekchen lesen ihren
   * Zustand selbst.
   */
  exportRows: SkillTableRow[] = [];

  isCategoryRow = (_index: number, row: SkillTableRow): boolean => 'group' in row;

  asSkill(row: SkillTableRow): IFaehigkeit {
    return row as IFaehigkeit;
  }

  asGroup(row: SkillTableRow): SkillGroup {
    return (row as {group: SkillGroup}).group;
  }

  private rebuildSkillGroups(): void {
    const grouped = new Map<string, IFaehigkeit[]>();
    for (const skill of this.skills ?? []) {
      const category = this.vocabulary.skillCategory(skill).trim();
      const bucket = grouped.get(category);
      if (bucket) {
        bucket.push(skill);
      } else {
        grouped.set(category, [skill]);
      }
    }
    this.skillGroups = this.vocabulary.sort([...grouped.entries()], ([category]) => category)
      .map(([category, skills]) => ({
        category,
        skills: this.vocabulary.sort(skills, skill => this.vocabulary.skillName(skill))
      }));

    this.exportRows = this.skillGroups.flatMap(group => [{group}, ...group.skills]);
  }

  /**
   * NP20-760: Unbewertete Faehigkeiten gehoeren nach oben, damit sie bewertet werden koennen.
   *
   * Frueher stieg die Methode aus, sobald ueberhaupt eine Sortierung aktiv war. Waren beim Laden
   * alle Faehigkeiten bewertet, stand die Liste alphabetisch — eine spaeter ueber ein Projekt
   * hinzugekommene, unbewertete Faehigkeit landete dann mitten in der Liste statt oben.
   *
   * Deshalb wird nur eine vom Nutzer gewaehlte Sortierung respektiert; die selbst gesetzte darf
   * nachziehen, wenn sich der Bewertungsstand aendert. Woher die aktuelle Sortierung kommt, sagt
   * autoSortedBy: null heisst "vom Nutzer", gesetzt durch das sortChange-Abo im sort-Setter.
   */
  private applyInitialSort(): void {
    if (!this.sort) {
      return;
    }

    const skillsData = this.dataSource.data ?? [];
    if (!skillsData.length) {
      return;
    }

    const userSorted = Boolean(this.sort.active && this.sort.direction && this.autoSortedBy === null);
    if (userSorted) {
      return;
    }

    const hasMissingLevels = skillsData.some((s) => this.isUnrated(s));
    const activeId = hasMissingLevels ? 'level' : 'skill';
    if (this.sort.active === activeId && this.sort.direction === 'asc') {
      return;
    }

    this.autoSortedBy = activeId;
    this.applyingAutoSort = true;
    try {
      this.sort.sort({
        id: activeId,
        start: 'asc',
        disableClear: true
      });
    } finally {
      this.applyingAutoSort = false;
    }
  }

  private refreshDataSource(): void {
    // Die Fundstellen haengen an den Objektidentitaeten aus `skills`; Abbrechen und Speichern
    // tauschen diese lokal gegen Klone, ohne ngOnChanges.
    this.indexFindings();
    this.dataSource.data = [...(this.skills ?? [])];
    this.rebuildSkillGroups();

    if (this.sort) {
      this.dataSource.sort = this.sort;
      this.applyInitialSort();
    }
  }

  save() {
    this.editMode = false;
    this.isSaving = true;
    this.beraterService
      .updateFaehigkeiten(this.skills, this.consultant.id)
      .subscribe({
        next: (response: IBerater) => {
          this.consultant = response;
          this.skills = this.consultant.faehigkeiten;
          this.skillsCopy = JSON.parse(JSON.stringify(this.skills));
          this.isSaving = false;
          this.refreshDataSource();
          this.checkFormChanged();
          this.projectSkill.emit();
          this.messageService.success('skills.successMessage');
        },
        error: () => {
          this.isSaving = false;
          this.refreshDataSource();
          this.checkFormChanged();
          this.messageService.error('skills.errorMessage');
        }
      });
  }

  onToggleEdit(): void {
    if (this.editMode) {
      this.save();
    } else {
      this.originalSkills = JSON.parse(JSON.stringify(this.skills));
      // NP20-912: Ab hier auf einer eigenen Kopie arbeiten. Die Bewertung schrieb sonst direkt in
      // die Objekte des Profils, und Abbrechen tauschte nur die Anzeige zurueck.
      this.skills = JSON.parse(JSON.stringify(this.skills));
      this.formChanged = false;
      this.editMode = true;
      this.refreshDataSource();
    }
  }

  isUnrated(skill: IFaehigkeit | null | undefined): boolean {
    return !skill?.level || skill.level === 'NICHT_VORHANDEN';
  }

  levelToStars(level: string | null): number {
    switch (level) {
      case 'BASIS':
        return 1;
      case 'GUT':
        return 2;
      case 'SEHR_GUT':
        return 3;
      case 'EXZELLENT':
        return 4;
      default:
        return 0;
    }
  }

  setLevelByStars(faehigkeit: IFaehigkeit, stars: number) {
    const levels = ['BASIS', 'GUT', 'SEHR_GUT', 'EXZELLENT'];
    faehigkeit.level = levels[stars - 1];
    this.checkFormChanged();
  }

  onCancelEdit() {
    this.skills = JSON.parse(JSON.stringify(this.originalSkills));
    this.editMode = false;
    this.refreshDataSource();
    this.checkFormChanged();
  }

  checkFormChanged() {
    this.formChanged =
      JSON.stringify(this.skillsCopy) !== JSON.stringify(this.skills);
  }
}
